/**
 * ContentBlocksCategoryPanel (Story 37.4 — Phase 2 category panel #3)
 *
 * The `content-blocks` sidebar category's floating-panel body: a
 * **configure-then-add** picker for the four page-element types
 * (`heading` / `paragraph` / `rich-text` / `spacer-divider`).
 *
 * 2026-06-13 redesign (Calin): the four type buttons are now **draggable
 * preview cards** with the exact PlatformPaletteCard anatomy (drag-handle dots,
 * label, scaled WYSIWYG `WidgetPreview` frame) so page elements read and behave
 * like the platform widgets — drag onto the canvas OR click to select +
 * configure + Add. Both paths produce a NON-BLANK block:
 *  - Click-to-select keeps the original configure-then-add flow (config section
 *    + Add button below the cards — AC #1/#4/#5 unchanged).
 *  - Drag carries a `config` payload on `PaletteDragData`: the live draft when
 *    this card is the selected type AND the draft validates, else the type's
 *    SAMPLE config (non-blank placeholder content). The canvas drop seeds it
 *    over `DEFAULT_WIDGET_CONFIGS[type]`, and the DragOverlay ghost renders the
 *    same config — dragged ≡ ghost ≡ landed (never blank, AC #5 preserved).
 *
 * Reuse, don't fork: the per-type config controls are the SHARED
 * `ContentBlockConfigSection` from `contentBlockForms` (the exact same sub-form
 * the `WidgetConfigPanel` uses when editing an existing content block — AC #2/#3
 * anti-drift). The sub-form's contract is `{ widget, onUpdate }`; this panel has
 * no real widget yet, so it wraps a local **draft config** in a synthetic
 * draft-widget and adapts `onUpdate` to merge patches into the draft (Pre-mortem
 * A). Card previews reuse the same draft, so the selected card's preview is live
 * WYSIWYG while you type.
 */

import { useState, useMemo, useCallback } from "react";
import { useDraggable } from "@dnd-kit/core";
import { TextT, DotsSixVertical } from "@phosphor-icons/react";
import type { WidgetConfig } from "../../lib/templateUtils";
import type { PaletteDragData } from "../../lib/reportDnd";
import { getSmartDefaults, type SmartDefaultWidgetType } from "../../lib/gridUtils";
import { DEFAULT_WIDGET_CONFIGS, DEFAULT_WIDGET_TITLES } from "./canvasWidgetDefaults";
import { WidgetPreview } from "./WidgetPreview";
import {
  ContentBlockConfigSection,
  isContentBlockDraftValid,
  type ContentBlockType,
} from "./contentBlockForms";
import type { CategoryPanelProps } from "./categoryPickerRegistry";

/** Synthetic draft-widget id — stable, never persisted (Pre-mortem A). */
const DRAFT_CONTENT_BLOCK_WIDGET_ID = "__draft_content_block__";

/** The four content-block types, in selector order, with their short labels. */
const CONTENT_BLOCK_TYPES: { type: ContentBlockType; label: string }[] = [
  { type: "heading", label: "Heading" },
  { type: "paragraph", label: "Paragraph" },
  { type: "rich-text", label: "Rich Text" },
  { type: "spacer-divider", label: "Spacer" },
];

/**
 * Non-blank SAMPLE config per type — what a card previews (and what a drag
 * drops) when the user hasn't configured that type. Mirrors the data-widget
 * cards, whose previews render sample data rather than empty frames.
 */
const SAMPLE_CONFIGS: Record<ContentBlockType, Record<string, unknown>> = {
  heading: { ...DEFAULT_WIDGET_CONFIGS.heading, content: "Section heading" },
  paragraph: {
    ...DEFAULT_WIDGET_CONFIGS.paragraph,
    content: "A short paragraph of supporting copy for this section of the report.",
  },
  "rich-text": {
    ...DEFAULT_WIDGET_CONFIGS["rich-text"],
    content: "Highlight a key takeaway for your client.",
    variant: "callout",
  },
  "spacer-divider": { ...DEFAULT_WIDGET_CONFIGS["spacer-divider"] },
};

/**
 * One draggable content-block card — the PlatformPaletteCard anatomy (handle
 * dots + label header, scaled WYSIWYG preview frame, dim-while-dragging) plus
 * click-to-select (aria-pressed + the original `content-block-type-{type}`
 * testid, so the configure-then-add flow and its tests are unchanged).
 */
function ContentBlockCard({
  type,
  label,
  selected,
  previewConfig,
  dragConfig,
  onSelect,
}: {
  type: ContentBlockType;
  label: string;
  selected: boolean;
  previewConfig: Record<string, unknown>;
  dragConfig: Record<string, unknown>;
  onSelect: () => void;
}) {
  const spans = getSmartDefaults(type as SmartDefaultWidgetType);
  const dragData: PaletteDragData = {
    source: "palette",
    type,
    colSpan: spans.colSpan,
    rowSpan: spans.rowSpan,
    label,
    config: dragConfig,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `content-block-palette-${type}`,
    data: dragData,
  });

  const previewWidget = useMemo<WidgetConfig>(
    () => ({
      id: `preview-${type}`,
      type,
      title: DEFAULT_WIDGET_TITLES[type],
      position: { row: 0, col: 0, colSpan: spans.colSpan, rowSpan: spans.rowSpan },
      config: previewConfig,
    }),
    [type, previewConfig, spans.colSpan, spans.rowSpan],
  );

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="listitem"
      aria-roledescription="draggable widget"
      aria-label={`Drag ${label} onto the canvas`}
      aria-pressed={selected}
      data-testid={`content-block-type-${type}`}
      onClick={onSelect}
      style={{
        opacity: isDragging ? 0.4 : undefined,
        touchAction: "none",
        border: selected ? "0.5px solid hsl(214 25% 55%)" : "0.5px solid var(--hairline)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.border = "0.5px solid hsl(214 25% 55%)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.border = selected
          ? "0.5px solid hsl(214 25% 55%)"
          : "0.5px solid var(--hairline)";
      }}
      className={`group flex flex-col gap-1.5 p-3 rounded-xl shadow-xs transition-all
        cursor-grab active:cursor-grabbing ${selected ? "bg-foreground/5" : "bg-background"}`}
    >
      <div className="flex items-center gap-2">
        <DotsSixVertical size={14} className="text-foreground shrink-0" />
        <span className="text-sm font-medium truncate">{label}</span>
      </div>
      {/* Scaled WYSIWYG preview — what this block looks like on the canvas
          (centered in its frame, same as the platform widget cards). */}
      <div
        className="flex justify-center rounded-lg bg-muted/30 overflow-hidden pointer-events-none"
        style={{ border: "0.5px solid var(--hairline)" }}
      >
        <WidgetPreview widget={previewWidget} scale={0.75} frameWidth={216} frameHeight={64} />
      </div>
    </div>
  );
}

export function ContentBlocksCategoryPanel({ onWidgetAdd }: CategoryPanelProps) {
  // Default selected type on open is `heading` (AC #6).
  const [selectedType, setSelectedType] = useState<ContentBlockType>("heading");
  // Seed the draft from the real default config so a configured-then-added block
  // equals what a blank drop + manual fill would produce. Pre-mortem D: seed
  // verbatim from DEFAULT_WIDGET_CONFIGS — do NOT invent a `height` for spacer.
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({
    ...DEFAULT_WIDGET_CONFIGS.heading,
  }));

  // Switching type reseeds the draft from that type's default (AC #6).
  const selectType = useCallback((t: ContentBlockType) => {
    setSelectedType(t);
    setDraft({ ...DEFAULT_WIDGET_CONFIGS[t] });
  }, []);

  // Synthetic draft-widget wrapping the local draft + the live selected type.
  // The sub-form only reads `id` + `type` + `config`, but TS needs the full
  // WidgetConfig shape, so fill the rest with throwaway-but-valid defaults.
  const draftWidget = useMemo<WidgetConfig>(
    () => ({
      id: DRAFT_CONTENT_BLOCK_WIDGET_ID,
      type: selectedType,
      title: DEFAULT_WIDGET_TITLES[selectedType],
      position: { row: 0, col: 0, colSpan: 12, rowSpan: 1 },
      config: draft,
    }),
    [selectedType, draft],
  );

  // Adapt the sub-form's `onUpdate(id, changes)` to merge `changes.config` into
  // the local draft (Pre-mortem A: the merge MUST land or Add stays disabled
  // forever for content-requiring types). The id is ignored — one draft.
  const handleDraftUpdate = useCallback(
    (_id: string, changes: Partial<WidgetConfig>) => {
      if (!changes.config) return;
      setDraft((d) => ({ ...d, ...(changes.config as Record<string, unknown>) }));
    },
    [],
  );

  // Per-type validity (AC #5): heading/paragraph/rich-text require non-empty
  // content; spacer-divider is always valid. AND a real add handler must be
  // wired — a missing `onWidgetAdd` would otherwise leave a button that wipes
  // the draft and does nothing (37.3 code-review R1).
  const valid = isContentBlockDraftValid(selectedType, draft);
  const canAdd = valid && typeof onWidgetAdd === "function";

  const handleAdd = useCallback(() => {
    if (!canAdd || !onWidgetAdd) return;
    const added = onWidgetAdd(selectedType, { ...draft });
    // Reset the draft ONLY if the widget was actually added — a rejected add
    // (page at MAX_WIDGETS) must not discard the user's work (37.3 R1 lesson).
    if (added) setDraft({ ...DEFAULT_WIDGET_CONFIGS[selectedType] });
  }, [canAdd, onWidgetAdd, selectedType, draft]);

  // Per-card preview/drag config: the selected card mirrors the LIVE draft when
  // it validates (WYSIWYG while typing); otherwise the type's non-blank sample.
  const configFor = useCallback(
    (t: ContentBlockType): Record<string, unknown> =>
      t === selectedType && isContentBlockDraftValid(t, draft)
        ? { ...draft }
        : { ...SAMPLE_CONFIGS[t] },
    [selectedType, draft],
  );

  return (
    <div className="flex h-full flex-col p-3" data-testid="content-blocks-category-panel">
      <div className="mb-2 flex items-center gap-2">
        <TextT size={18} className="text-muted-foreground" />
        <h3 className="text-[13px] font-semibold text-foreground">Content Blocks</h3>
      </div>

      <p className="mb-2 text-[11px] text-muted-foreground">Drag onto the canvas</p>

      <div className="flex-1 overflow-y-auto">
        {/* Draggable preview cards — platform-widget card anatomy (AC #6 type
            selection preserved via click; role="group" + aria-label so the four
            mutually-exclusive cards read as one labelled selection group). */}
        <div
          className="flex flex-col gap-3"
          data-testid="content-block-type-selector"
          role="group"
          aria-label="Content block type"
        >
          {CONTENT_BLOCK_TYPES.map(({ type, label }) => (
            <ContentBlockCard
              key={type}
              type={type}
              label={label}
              selected={selectedType === type}
              previewConfig={configFor(type)}
              dragConfig={configFor(type)}
              onSelect={() => selectType(type)}
            />
          ))}
        </div>

        <ContentBlockConfigSection widget={draftWidget} onUpdate={handleDraftUpdate} />
      </div>

      <button
        type="button"
        data-testid="content-blocks-panel-add-btn"
        disabled={!canAdd}
        onClick={handleAdd}
        className="mt-3 w-full rounded-md border border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Add to report
      </button>
    </div>
  );
}
